/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Typography } from 'antd';
import {
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TablePagination
} from '@material-ui/core';
import PaymentHistModal from '../payment_hist_modal/index';
import { fetchInvoiceHistory } from 'services/payments/payments';
import { logAuditTrailEvent } from 'services/system_setup/auditTrail';
import '../payments.css';
import { GLOBAL_TABLE_PAGE_SIZE } from 'global_config/constant-global';
import { TABLE_HEADER_STYLE, TABLE_CELL_STYLE, TABLE_PAGINATION_STYLE, TABLE_SORT_LABEL_STYLE } from 'utils/tableStyles';

function parseDateValue(value) {
    if (!value) return NaN;
    if (value instanceof Date) return value.getTime();

    const normalized = String(value).trim();
    if (!normalized) return NaN;

    const parsed = Date.parse(normalized);
    if (!Number.isNaN(parsed)) return parsed;

    const mdy = normalized.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (mdy) {
        const month = Number(mdy[1]) - 1;
        const day = Number(mdy[2]);
        const year = Number(mdy[3]);
        return new Date(year, month, day).getTime();
    }

    return NaN;
}


function SearchTable(props) {
    const { data = [], pageSize = GLOBAL_TABLE_PAGE_SIZE } = props;
    const history = useHistory();
    const [showModal, setShowModal] = useState(false);
    const [invHistData, setInvHistData] = useState({});
    
    // Sorting and pagination state
    const [orderBy, setOrderBy] = useState('payment_date');
    const [order, setOrder] = useState('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pageSize);

    // Sync rowsPerPage with pageSize prop and reset page when pageSize changes
    useEffect(() => {
        setRowsPerPage(pageSize);
        setPage(0);
    }, [pageSize]);

    async function showInvoiceHistory (data){
        const invoiceHistory = await fetchInvoiceHistory(data);
        // console.table(invoiceHistory);
        setInvHistData(invoiceHistory);
        setShowModal(true);
    };


    const dataSource = (Array.isArray(data) ? data : []).map(rec =>({
        key: rec.key,
        receipt_no: rec.receipt_no,
        payment_date: rec.payment_date,
        invoice_no: <a href="" onClick={(event) =>{
            event.preventDefault();
            showInvoiceHistory(rec.invoice_no);
        }}>{rec.invoice_no}</a>, //BE ADVISED!!! making changes in this attribute greatly affects the local function: handleUpdate
        customer: rec.customer_name,
        status_name: rec.status_name,
        //
        payment_id: rec.payment_id,
        or_no: rec.receipt_no,
        payment_method_id: rec.payment_method_id,
        payment_method_name: rec.payment_method_name,
        bank_id: rec.bank_id,
        bank_name:rec.bank_name,
        check_no:  rec.check_no,
        remittance_advice_no: rec.remittance_advice_no,
        customer_id: rec.customer_id,
        vat_input_id: rec.vat_input_id,
        vat_input_name: rec.vat_input_name,
        tax_id_no: rec.tax_id_no,
        billing_address: rec.billing_address,
        collected_by: rec.collected_by,
        remarks: rec.remarks,
        encoded_by: rec.encoded_by, 
        total_vat_amount: rec.total_vat_amount,
        total_payment_amount: rec.total_payment_amount,
        total_ewt_amount: rec.total_ewt_amount,
        vat_amount: rec.total_vat_amount,
        invoice_type_id: rec.invoice_type_id,
        status_id: rec.status_id,
        created_by: rec.created_by,
        encoded_by_name: rec.encoded_by_name,
        amount_in_word: rec.amount_in_word, 
        vatable_sales: rec.vatable_sales,
        //added by pabs
        agent_id: rec.agent_id,
        house_own: rec.house_own,
        total_amount_due: rec.total_amount_due,
    }));
    
    const columns = [
        {
            title: "O.R. NO.",
            dataIndex: "receipt_no",
            key: "receipt_no",
            width: "15%",
            sortable: true
        },
        {
            title: "PAYMENT DATE",
            dataIndex: "payment_date",
            key: "payment_date",
            width: "10%",
            sortable: true
        },
        {
            title: "INVOICE NO.",
            dataIndex: "invoice_no",
            key: "invoice_no",
            width: "15%",
            sortable: true
        },
        {
            title: "CUSTOMER",
            dataIndex: "customer",
            key: "customer",
            width: "45%",
            sortable: true
        },
        {
            title: "STATUS",
            dataIndex: "status_name",
            key: "status",
            width: "10%",
            sortable: true
        },
    ]; 

    // Sorting helpers
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedData = useMemo(() => {
        const copy = [...dataSource];
        if (!orderBy) return copy;
        return copy.sort((a, b) => {
            if (orderBy === 'payment_date') {
                const aDate = parseDateValue(a.payment_date);
                const bDate = parseDateValue(b.payment_date);

                if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
                    return order === 'asc' ? aDate - bDate : bDate - aDate;
                }
            }

            // Handle invoice_no special case (React element)
            let av, bv;
            if (orderBy === 'invoice_no') {
                av = typeof a[orderBy] === 'object' ? a[orderBy].props.children : a[orderBy];
                bv = typeof b[orderBy] === 'object' ? b[orderBy].props.children : b[orderBy];
            } else {
                av = (a[orderBy] || '').toString();
                bv = (b[orderBy] || '').toString();
            }
            const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
            return order === 'asc' ? cmp : -cmp;
        });
    }, [dataSource, orderBy, order]);

    const pagedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const handleChangePage = (_event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    async function handleUpdate(rec){
        //important!!
        //done code below to replace value of invoice to text instead of element when redirecting the user to update screen
        if (typeof rec.invoice_no === 'object') {
            rec.invoice_no = rec.invoice_no.props.children;
        }
        void logAuditTrailEvent({
            moduleCode: 'pymt',
            subModuleCode: 'search',
            action: 'VIEW',
            description: `View payment record ${rec && rec.receipt_no ? rec.receipt_no : ''}`.trim(),
            payload: {
                payment_id: rec && rec.payment_id,
                receipt_no: rec && rec.receipt_no,
            },
            isSuccess: 1,
        });
        console.log('rec', rec)
        history.push('/payment/update_payment', rec);
    }

    return(
        <div>
            <TableContainer component={Paper} style={{ marginTop: 16 }}>
                <MuiTable>
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell 
                                    key={col.key || col.dataIndex} 
                                    style={{ ...TABLE_HEADER_STYLE, width: col.width }}
                                >
                                    {col.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === col.dataIndex}
                                            direction={orderBy === col.dataIndex ? order : 'asc'}
                                            onClick={() => handleRequestSort(col.dataIndex)}
                                            style={TABLE_SORT_LABEL_STYLE}
                                        >
                                            {col.title}
                                        </TableSortLabel>
                                    ) : (
                                        col.title
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedRows.map((row, idx) => (
                            <TableRow
                                key={row.key || row.receipt_no || idx}
                                hover
                                onDoubleClick={() => handleUpdate(row)}
                                style={{ textTransform: 'uppercase', cursor: 'pointer' }}
                            >
                                {columns.map((col) => (
                                    <TableCell 
                                        key={col.key || col.dataIndex}
                                        style={TABLE_CELL_STYLE}
                                    >
                                        {col.dataIndex === 'invoice_no' ? (
                                            <Typography.Link
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    showInvoiceHistory(typeof row[col.dataIndex] === 'object' ? 
                                                        row[col.dataIndex].props.children : row[col.dataIndex]);
                                                }}
                                                style={{ color: '#1890ff', textDecoration: 'underline' }}
                                            >
                                                {typeof row[col.dataIndex] === 'object' ? 
                                                    row[col.dataIndex].props.children : row[col.dataIndex]}
                                            </Typography.Link>
                                        ) : (
                                            row[col.dataIndex]
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {pagedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" style={TABLE_CELL_STYLE}>
                                    No records found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </MuiTable>
            </TableContainer>
            <TablePagination
                component="div"
                count={dataSource.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[]}
                style={TABLE_PAGINATION_STYLE}
            />
            <PaymentHistModal 
                visible={showModal} 
                data={invHistData}
                hideModal={()=>{setShowModal(false)}}                
            />
        </div>
    );
}
export default SearchTable;
